import { memo, useEffect, useRef, useState } from 'react';
import { useReactFlow, type NodeProps } from '@xyflow/react';
import type { FlowNode } from '../graph/types';

const TAMANOS: Record<string, string> = {
  chico: 'text-[15px] font-medium',
  mediano: 'text-[20px] font-semibold',
  grande: 'text-[28px] font-semibold',
};

/** Texto suelto (título de un flujito, indicación). Se edita con doble click. */
export function NodoTexto({ id, data, selected }: NodeProps<FlowNode>) {
  const { updateNodeData } = useReactFlow();
  const texto = String(data.params.texto ?? '');
  const tamano = String(data.params.tamano ?? 'grande');
  const [editando, setEditando] = useState(!texto);
  const ref = useRef<HTMLTextAreaElement>(null);
  const ajustar = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  };
  // El foco va con un pequeño retraso: React Flow enfoca el nodo recién creado después
  // del montaje y nos lo robaba (por eso "había que volver a tocar el placeholder").
  useEffect(() => {
    if (!editando) return;
    ajustar();
    const t = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }, 60);
    return () => window.clearTimeout(t);
  }, [editando, tamano]);

  const clases = `${TAMANOS[tamano] ?? TAMANOS.grande} leading-tight text-brand-black/90`;
  return (
    <div className={`relative rounded-xl px-2 py-1 ${selected ? 'ring-2 ring-brand-primary/30' : ''}`} style={{ minWidth: 120 }} onDoubleClick={() => setEditando(true)}>
      {selected && (
        <div className="nodrag absolute -top-8 left-0 flex gap-1 rounded-full border border-black/[0.06] bg-white p-0.5 shadow-[0_6px_20px_rgba(2,6,43,0.08)]">
          {(['chico', 'mediano', 'grande'] as const).map((t) => (
            <button key={t} type="button" onClick={() => updateNodeData(id, { params: { ...data.params, tamano: t } })} className={`h-6 rounded-full px-2 text-[10.5px] font-medium ${tamano === t ? 'bg-[#E8EEFF] text-brand-primary' : 'text-neutral-500 hover:bg-surface-elevated'}`}>
              {t === 'chico' ? 'S' : t === 'mediano' ? 'M' : 'L'}
            </button>
          ))}
        </div>
      )}
      {editando ? (
        <textarea
          ref={ref}
          value={texto}
          placeholder="Escribí un título…"
          onChange={(e) => {
            updateNodeData(id, { params: { ...data.params, texto: e.target.value } });
            ajustar();
          }}
          onBlur={() => setEditando(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setEditando(false);
            }
          }}
          rows={1}
          className={`nodrag w-full resize-none overflow-hidden bg-transparent outline-none placeholder:text-brand-black/30 ${clases}`}
          style={{ minWidth: 280 }}
        />
      ) : (
        <div className={`whitespace-pre-wrap ${clases}`} title="Doble click para editar" onClick={() => !texto && setEditando(true)}>
          {texto || <span className="text-brand-black/30">Escribí un título…</span>}
        </div>
      )}
    </div>
  );
}

export default memo(NodoTexto);
